// Batch-level Documents tab APIs (Documents Tab - Functional Specs).
//
//   • Section/row data  -> POST /fetchPDBatchDocuments
//   • Review window     -> POST /fetchPDDocumentReview
//   • Download / PDF    -> POST /downloadPDCustomFile (re-exported from the
//                          Document Repository, which already owns it — the two
//                          screens download PD digitized documents the same way)
//
// All three are REAL endpoints; nothing here is stubbed. Failures resolve to an
// empty/false result rather than throwing, matching every other PD dashboard API
// module, so a tab renders its own "no data" state instead of breaking the page.

import axios from 'axios';
import { appUrl } from '../../../../../../../utils/config';
import { getApiBaseUrl } from '../../../CommonMethods/CommonMethods';

export { downloadPDCustomFileAPI } from '../../DocumentRepository/documentRepositoryAPI';

const EMPTY_SECTIONS = { sections: [], confidenceLimit: null };

/**
 * Documents for one batch, already grouped into the FSD's numbered sections.
 *
 * @param {Object}  scope
 * @param {string}  scope.campaignID  business Campaign ID (e.g. "CMPO04BRS729"),
 *                                    NOT the collabrixCampaignID — the document
 *                                    rows are keyed on the business ID.
 * @param {string}  scope.batchNumber CMO batch number (e.g. "DBSQA25001").
 * @returns {Promise<{status: boolean, data: {sections: Array, confidenceLimit: ?number}}>}
 *
 * Section shape: { key, title, recordIdLabel, groups: [{ recordId, documents: [...] }] }
 * Sections arrive in display order (most recently updated first) — the numbered
 * rail is just their index + 1, so the FE must not re-sort them.
 */
export const fetchPDBatchDocumentsAPI = async ({ campaignID, batchNumber }) => {
  try {
    if (!campaignID || !batchNumber) {
      return { status: false, data: EMPTY_SECTIONS };
    }
    const apiBaseUrl = getApiBaseUrl(appUrl);
    if (!apiBaseUrl) return { status: false, data: EMPTY_SECTIONS };

    const response = await axios.post(`${apiBaseUrl}/fetchPDBatchDocuments`, {
      campaignID,
      batchNumber,
    });
    if (response?.data?.status === true) {
      return {
        status: true,
        data: {
          sections: response.data.data?.sections || [],
          confidenceLimit: response.data.data?.confidenceLimit ?? null,
        },
      };
    }
    return { status: false, data: EMPTY_SECTIONS };
  } catch (error) {
    console.error('[DocumentsTab] Failed to load batch documents:', error);
    return { status: false, data: EMPTY_SECTIONS };
  }
};

/**
 * One document's digitalized fields for the split-screen review window's LEFT
 * pane, with each field's extractor confidence and review state. The RIGHT pane
 * (the source PDF) is loaded separately from the returned collabrixPDCustomFileID
 * via downloadPDCustomFileAPI, so the window paints before the PDF arrives.
 *
 * @param {string} collabrixPDDigitizedDocumentID the row's Mosymphony ID ("MSY-00000123")
 */
export const fetchPDDocumentReviewAPI = async (collabrixPDDigitizedDocumentID) => {
  try {
    if (!collabrixPDDigitizedDocumentID) return { status: false, data: null };
    const apiBaseUrl = getApiBaseUrl(appUrl);
    if (!apiBaseUrl) return { status: false, data: null };

    const response = await axios.post(`${apiBaseUrl}/fetchPDDocumentReview`, {
      collabrixPDDigitizedDocumentID,
    });
    if (response?.data?.status === true) {
      return { status: true, data: response.data.data };
    }
    return { status: false, data: null, message: response?.data?.message };
  } catch (error) {
    console.error('[DocumentsTab] Failed to load document review:', error);
    return { status: false, data: null };
  }
};
